import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuthedObjectUrl } from "@/hooks/useAuthedObjectUrl";
import {
  Loader2,
  Download,
  ZoomIn,
  ZoomOut,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

interface LightboxImage {
  src: string;
  alt: string;
  fileName: string;
  /** Set when known; a PDF proof is displayed inline instead of as an image. */
  contentType?: string;
}

interface PaymentProofLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaymentProofLightbox({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
}: PaymentProofLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  const current = images[currentIndex];
  const isPdf = current?.contentType?.toLowerCase() === "application/pdf";

  // Full size, so no thumbnail width: this is where the operator reads the account number off
  // the slip. Going through the shared hook means the file the table already downloaded for its
  // tile is not downloaded again on the way into the lightbox, and the Download button below
  // saves the blob that is already on screen rather than fetching a third copy.
  const {
    objectUrl,
    isLoading: loading,
    isError: error,
    refetch,
  } = useAuthedObjectUrl(current?.src, open);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : images.length - 1));
    setZoom(1);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => (i < images.length - 1 ? i + 1 : 0));
    setZoom(1);
  }, [images.length]);

  useEffect(() => {
    if (!open) {
      setZoom(1);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "ArrowRight") handleNext();
      else if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handlePrev, handleNext, onOpenChange]);

  const handleDownload = useCallback(() => {
    if (!current || !objectUrl) {
      // Nothing on screen yet, so there is nothing to save. The button is disabled in that
      // state; this only guards a keyboard activation that races the download.
      toast.error("Failed to download file");
      return;
    }

    // The bytes are already here. Fetching them again — which is what this did — meant a second
    // copy of a file of up to 5 MB for a click made while looking at the first.
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = current.fileName;
    link.click();
  }, [current, objectUrl]);

  const toggleZoom = useCallback(() => {
    setZoom((z) => (z >= 2 ? 1 : z + 0.5));
  }, []);

  // Each zoom step keeps the middle of the slip in view; left alone the stage would stay scrolled
  // to its top-left corner.
  const stageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
    stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
  }, [zoom]);

  if (images.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The toolbar carries its own close button next to zoom and download, so the dialog's
          corner X is switched off — with both, two X icons overlap at the top-right. */}
      <DialogContent
        showCloseButton={false}
        className="w-[min(94vw,960px)] max-w-none p-0 gap-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">
          Payment Proof — {current?.alt}
        </DialogTitle>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
          <span className="text-sm text-muted-foreground">
            {currentIndex + 1} / {images.length}
            {current && ` — ${current.alt}`}
          </span>
          <div className="flex items-center gap-1">
            {!isPdf && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleZoom}
                title={zoom >= 2 ? "Zoom out" : "Zoom in"}
              >
                {zoom >= 2 ? (
                  <ZoomOut className="h-4 w-4" />
                ) : (
                  <ZoomIn className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleDownload}
              // Nothing to save until the bytes are here — the handler now saves what is on
              // screen rather than starting a download of its own.
              disabled={!objectUrl}
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange(false)}
              title="Close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Image area — a fixed stage, whatever the file. Sized to its content it jumped between a
            400px strip around a small screenshot and 70vh around a large scan, and a small slip
            stayed small: the file was shown at its own pixel size, never fitted to the space. */}
        <div className="relative h-[min(78vh,760px)] bg-black/90">
          <div ref={stageRef} className="scrollbar-thin h-full w-full overflow-auto">
            {objectUrl && !loading && isPdf && (
              <iframe
                src={objectUrl}
                title={current?.fileName ?? "Payment proof"}
                className="h-full w-full bg-white"
              />
            )}
            {objectUrl && !loading && !isPdf && (
              // Zoom grows the box the image is fitted into, not a CSS scale: a transform leaves
              // the layout size alone, so the scroll area never grew and the enlarged edges were
              // clipped out of reach.
              <div
                style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}
              >
                <img
                  src={objectUrl}
                  alt={current?.alt}
                  className="h-full w-full object-contain"
                />
              </div>
            )}
          </div>

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white text-sm">
              <span>Failed to load file</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                onClick={handlePrev}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                onClick={handleNext}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
